import React, { useEffect, useState } from "react";
import ReactGA from "react-ga4";
import { formatLeadTime } from "../utils/formatters";
import { config } from "../config";
import InfoModal from "./InfoModal";
import { formatPhoneNumber } from "../utils/formatters";
import QuantityInput from './QuantityInput';

function Cart({
  cart,
  store,
  onUpdateQuantity,
  onRemoveItem,
  onProceed,
  theme,
  total,
  productTotal,
  tax,
  discountCode,
  setDiscountCode,
  setDiscountRate,
  discount,
  setDiscountCodeId,
}) {
  const [showVariablePriceModal, setShowVariablePriceModal] = useState(false);

  const checkForVariablePricing = () => {
    return cart.some(item => 
      item.price_type && item.price_type.toLowerCase() === "variable"
    );
  };

  useEffect(() => {
    const hasVariablePricing = cart.some(item => 
      item.price_type && item.price_type.toLowerCase() === "variable"
    );
    
    if (hasVariablePricing) {
      setShowVariablePriceModal(true);
    }
  }, [cart]);

  const VariablePriceModal = () => (
    <InfoModal
      show={showVariablePriceModal}
      onClose={() => setShowVariablePriceModal(false)}
      title="Variable Pricing Notice"
      theme={theme}
    >
      <p>
        Items with variable pricing cannot be ordered online.
        <br /><br />
        Please contact us directly at <a href={`tel:${store?.display_phone}`} style={{ color: theme.primaryFontColor }}>
          {store?.display_phone ? formatPhoneNumber(store?.display_phone) : ''}
        </a> if you need this order sooner.
      </p>
    </InfoModal>
  );

  async function handleApplyDiscount() {
    try {
      if (!discountCode.trim()) {
        alert("Please enter a discount code.");
        return;
      }
      const response = await fetch(
        `${config.apiUrl}/get-discount-by-code`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            code: discountCode,
            store_id: store.store_id,
          }),
        }
      );
      if (!response.ok)
        throw new Error(`Server responded with status: ${response.status}`);
      const data = await response.json();

      console.log("discount data::::", data); //TESTING

      if (data.data && data.data["discount_rate"] !== undefined) {
        setDiscountRate(data.data["discount_rate"]);
        setDiscountCodeId(data.data["id"]);
        // alert("Discount applied successfully!");
      } else {
        setDiscountCode("");
        setDiscountRate(0.0);
        alert("Invalid discount code.");
      }
    } catch (error) {
      setDiscountCode("");
      setDiscountRate(0.0);
      console.error("Error applying discount:", error);
      alert("Failed to apply discount. Please try again later.");
    }
  }

  const handleRemoveItem = (item) => {
    ReactGA.event({
      category: "ecommerce",
      action: "remove_from_cart",
      label: item.title,
      value: item.retail_price * item.quantity,
    });
    onRemoveItem(item.store_items_id);
  };

  const handleUpdateQuantity = (item, newQuantity) => {
    ReactGA.event({
      category: "ecommerce",
      action: "update_quantity",
      label: item.title,
      value: item.retail_price * newQuantity,
    });
    onUpdateQuantity(item.store_items_id, newQuantity);
  };

  const handleProceed = () => {
    ReactGA.event({
      category: "ecommerce",
      action: "begin_checkout",
      value: total,
    });
    if (checkForVariablePricing()) {
      setShowVariablePriceModal(true);
    } else if (total < 0.50) {
      alert("The minimum order amount is $0.50. Please add more items to your cart.");
    } else {
      onProceed();
    }
  };

  const styles = {
    container: {
      display: "flex",
      flexDirection: "column",
      height: "100%",
    },
    title: {
      color: theme.primaryFontColor,
      fontSize: "20px",
      marginBottom: "20px",
    },
    subtotal: {
      color: theme.primaryFontColor,
      // marginRight: 20,
      fontSize: 14,
      // fontWeight: "bold",
      marginTop: -7,
    },
    table: {
      width: "100%",
      justifyContent: "space-between",
      display: "flex",
    },

    th: {
      color: theme.primaryFontColor,
      textAlign: "left",
      paddingBottom: "10px",
    },
    td: {
      textAlign: "left",
      color: "black",
      fontSize: 12,
      fontWeight: "bold",
    },
    input: {
      width: "50px",
      padding: "5px",
      backgroundColor: theme.backgroundColor,
      color: "black",
      border: `1px solid ${theme.primaryFontColor}`,
      borderRadius: "5px",
      fontSize: 12,
      textAlign: "center",
    },
    removeButton: {
      backgroundColor: "#dc3545",
      color: "white",
      border: "none",
      padding: "5px 10px",
      borderRadius: "5px",
      cursor: "pointer",
    },
    button: {
      backgroundColor: theme.accentColor,
      color: "white",
      border: "none",
      padding: "10px 15px",
      borderRadius: "5px",
      cursor: "pointer",
      width: 150,
    },
    footer: {
      position: "absolute",
      bottom: "0",
      left: "0",
      right: "0",
      padding: "10px 20px",
      display: "flex",
      justifyContent: "space-between",
      backgroundColor: "white",
      borderTop: "1px solid #ccc",
      borderBottomLeftRadius: "10px",
      borderBottomRightRadius: "10px",
      // backgroundColor: "red",
    },
    subtotalContainer: {
      display: "flex",
      alignSelf: "flex-end",
      flexDirection: "column",
      maxWidth: "200px",
      flex: "1 1 200px",
      marginBottom: 100,
      marginLeft: "10px",
    },
    subtotalRow: {
      display: "flex",
      alignItems: "flex-start",
      marginBottom: "5px",
      width: "100%",
    },
    subtotalLabel: {
      textAlign: "left",
      marginRight: "10px",
      flex: "1 1 auto",
      minWidth: "0",
    },
    subtotalValue: {
      textAlign: "right",
      flex: "0 0 auto",
    },
    quantityButton: {
      minWidth: '15px',
      height: '32px',
      padding: 0,
      fontSize: '18px',
      backgroundColor: theme.accentColor,
      color: 'white',
      borderRadius: '4px',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    },
  };

  return (
    <div style={styles.container}>
      <VariablePriceModal />
      <h3 style={styles.title}>Cart</h3>
      <div
        name="divider"
        style={{
          backgroundColor: theme.primaryFontColor,
          height: 1,
          marginBottom: 20,
        }}
      />

      {cart.length === 0 ? (
        <div style={{ 
          display: "flex", 
          flexDirection: "column", 
          alignItems: "center", 
          justifyContent: "center", 
          flex: 1,
          color: "black",
          textAlign: "center",
          padding: "20px"
        }}>
          <p style={{ fontSize: "18px", marginBottom: "12px", color: theme.primaryFontColor }}>Your cart is empty</p>
          <p style={{ fontSize: "14px" }}>Add items to your cart to get started!</p>
        </div>
      ) : (
        <>
          <table style={{ ...styles.table, tableLayout: "fixed", width: "100%" }}>
            <thead>
              <tr style={{ display: "table", width: "100%", tableLayout: "fixed" }}>
                <th style={{ ...styles.th, width: "10%", minWidth: "30px" }}></th>
                <th style={{ ...styles.th, width: "35%", minWidth: "60px" }}>Item</th>
                <th style={{ ...styles.th, width: "15%", minWidth: "50px", textAlign: "center" }}>Quantity</th>
                <th style={{ ...styles.th, width: "20%", minWidth: "50px", textAlign: "center" }}>Lead Time</th>
                <th style={{ ...styles.th, width: "10%", minWidth: "30px", textAlign: "right" }}>Price</th>
              </tr>
            </thead>
          </table>

          <div
            name="scrollable-content"
            style={{
              display: "flex",
              flexDirection: "column",
              flex: 1,
              overflowY: "auto",
              paddingBottom: 15,
              width: "100%",
            }}
          >
            <table style={{ ...styles.table, tableLayout: "fixed", width: "100%" }}>
              <tbody>
                {cart.map((item) => (
                  <tr
                    key={item.store_items_id}
                    style={{
                      display: "table",
                      width: "100%",
                      tableLayout: "fixed",
                    }}
                  >
                    <td style={{ ...styles.td, width: "10%", minWidth: "30px" }}>
                      <button
                        style={styles.removeButton}
                        onClick={() => handleRemoveItem(item)}
                      >
                        &times;
                      </button>
                    </td>
                    <td style={{ ...styles.td, width: "35%", minWidth: "60px" }}>
                      {item.title}
                    </td>
                    <td style={{ ...styles.td, width: "15%", minWidth: "50px", textAlign: "center" }}>
                      <QuantityInput 
                        value={item.quantity}
                        onBlur={(numVal) => {
                          handleUpdateQuantity(item, numVal);
                        }}
                        onRemove={() => {
                          onRemoveItem(item.store_items_id);
                        }}
                        theme={theme}
                        className="quantity-input"
                      />
                    </td>
                    <td style={{ ...styles.td, width: "20%", minWidth: "20px", textAlign: "center" }}>
                      {formatLeadTime(item.lead_time_hours)}
                    </td>
                    <td
                      style={{
                        ...styles.td,
                        width: "10%",
                        minWidth: "30px",
                        textAlign: "right",
                      }}
                    >
                      {item.price_type && item.price_type.toLowerCase() === "variable" ? (
                        "Variable"
                      ) : (
                        <>
                          ${Number(
                            item.retail_price.replace(/,/g, "") * item.quantity
                          ).toFixed(2)}
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div style={{ display: "flex", flexDirection: "row", justifyContent: "space-between", paddingTop: 18 }}>
            <div style={{ flex: 1 }} /> {/* Spacer to push subtotal to the right */}
            <div
              name="subtotal-container"
              style={styles.subtotalContainer}
            >
              <div style={styles.subtotalRow}>
                <p style={{...styles.subtotal, ...styles.subtotalLabel, color: "black"}}>
                  Subtotal:
                </p>
                <p style={{...styles.subtotal, ...styles.subtotalValue}}>
                  <strong style={{ color: theme.primaryFontColor }}>
                    ${productTotal?.toFixed(2) || "0.00"}
                  </strong>
                </p>
              </div>
              <div style={styles.subtotalRow}>
                <p style={{...styles.subtotal, ...styles.subtotalLabel, color: "black"}}>
                  Discount:
                </p>
                <p style={{...styles.subtotal, ...styles.subtotalValue}}>
                  <strong style={{ color: theme.primaryFontColor }}>
                    - ${discount.toFixed(2)}
                  </strong>
                </p>
              </div>

              <div style={styles.subtotalRow}>
                <p style={{...styles.subtotal, ...styles.subtotalLabel, color: "black"}}>
                  Tax:
                </p>
                <p style={{...styles.subtotal, ...styles.subtotalValue}}>
                  <strong style={{ color: theme.primaryFontColor }}>
                    ${tax.toFixed(2)}
                  </strong>
                </p>
              </div>
            </div>
          </div>
        </>
      )}

      {cart.length > 0 && (
        <div style={styles.footer}>
          <div name="footer-content" style={{ flex: 2, textAlign: "right" }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                width: "100%",
                justifySelf: "flex-end",
                // backgroundColor: "red",
              }}
            >
              <div
                name="promo-code-container"
                style={{
                  maxWidth: "35%",
                  minWidth: "200px",
                  // justifyContent: "flex-start",
                  // alignItems: "flex-start",
                  display: "flex",
                  gap: "8px",
                  flex: "0 1 auto", // grow=0, shrink=1, basis=auto
                }}
              >
                <input
                  type="text"
                  value={discountCode}
                  onChange={(e) => setDiscountCode(e.target.value)}
                  placeholder="Pro Discount Code"
                  required
                  style={{
                    padding: "8px",
                    border: "1px solid #ccc",
                    borderRadius: "5px",
                    flex: "1 1 auto",
                    minWidth: "0",
                  }}
                />
                <button
                  style={{ ...styles.button, width: 35, height: 35 }}
                  onClick={handleApplyDiscount}
                >
                  &gt;
                </button>
              </div>

              <h4
                style={{
                  color: "black",
                  marginBottom: "10px",
                  fontWeight: "400",
                }}
              >
                Cart Total:
              </h4>
              <h4
                style={{
                  color: theme.primaryFontColor,
                  marginBottom: "10px",
                  textAlign: "right",
                }}
              >
                ${(productTotal + tax - discount).toFixed(2)}
              </h4>
            </div>
            <div
              style={{
                // backgroundColor: "red",
                // width: "100%",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "10px",
              }}
            >
              <div></div>
              <button style={styles.button} onClick={handleProceed}>
                Next
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Cart;
